import { buildHeader, buildSecureApiHeader, buildQueryParam } from "./baseService";
import { getJson, getDeviceParams } from "../utils/httpHelper";


const withDeviceParams = (data) => {
  const device = getDeviceParams();
  if (!device.deviceId) return data;

  let payload;
  try {
    payload = data ? JSON.parse(data) : {};
  } catch (err) {
    // Body is not JSON — leave it untouched rather than risk corrupting it.
    return data;
  }

  if (device.deviceType && payload.deviceType === undefined) {
    payload.deviceType = device.deviceType;
  }
  if (payload.deviceId === undefined) {
    payload.deviceId = device.deviceId;
  }

  if (payload.device_id === undefined) {
    payload.device_id = device.deviceId;
  }
  if (payload.is_auto_login === undefined) {
    payload.is_auto_login = true;
  }
  if (device.deviceName && payload.device_name === undefined) {
    payload.device_name = device.deviceName;
  }

  return JSON.stringify(payload);
};

/** Load Stripe.js and return Stripe instance. Resolves when script is ready. */
export const loadStripeJs = (publishableKey) => {
  return new Promise((resolve) => {
    if (typeof window.Stripe !== "undefined") {
      resolve(window.Stripe(publishableKey));
      return;
    }
    const script = document.createElement("script");
    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    script.onload = () => resolve(window.Stripe(publishableKey));
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
};

export const getSubscriptionPlans = async (baseUrl, apiKey, channelId, params) => {
  const subscriptionPlans = await fetch(
    buildQueryParam(`${baseUrl}/subscription-plan/${channelId}/list/`, params),  
    {  
      method: "GET",
      headers: buildHeader(apiKey),
    }
  );
  return getJson(subscriptionPlans);
};

// Tenant toggle: show the coupon field on free plans / free PPV items. Keyed off the
// Tenant-Key the payment form already holds -- the site has no tenant UUID to hand,
// so /clients/config/<uuid>/ is not reachable from here.
const couponOnFreeItemsCache = {};

export const fetchCouponOnFreeItemsEnabled = (baseUrl, apiKey) => {
  if (!baseUrl || !apiKey) {
    return Promise.resolve(false);
  }

  const cacheKey = `${baseUrl}::${apiKey}`;
  if (!couponOnFreeItemsCache[cacheKey]) {
    couponOnFreeItemsCache[cacheKey] = fetch(
      `${baseUrl}/billing/offer/free-item-coupon-visibility/`,
      {
        method: "GET",
        headers: buildHeader(apiKey),
      }
    )
      .then((response) => getJson(response))
      .then((body) => !!body?.show_coupon_field_on_free_items)
      .catch((err) => {
        // Drop the cache entry so a transient failure doesn't disable the coupon
        // field for the rest of the session -- the next payment form retries.
        delete couponOnFreeItemsCache[cacheKey];
        console.warn(
          "[Coupon] free-item coupon visibility fetch failed:",
          err.message
        );
        return false;
      });
  }

  return couponOnFreeItemsCache[cacheKey];
};

export const processSubscriptionCheckout = async (baseUrl, data) => {
  const response = await fetch(
    `${baseUrl}/billing/subscription/subscribe/`,
    {
      method: "POST",
      headers: buildSecureApiHeader(),
      body: withDeviceParams(data),
    }
  );
  const responseJson = getJson(response);
  if (response.status !== 200) {
    return {
      status: "F",
      message: responseJson.message,
    };
  }
  return { status: "S", message: responseJson };
};

export const verifyCouponCode = async (baseUrl, apiKey, data) => {
  const response = await fetch(
    `${baseUrl}/billing/offer/verify/`,
    {
      method: "POST",
      headers: buildHeader(apiKey),
      body: data,
    }
  );

  return getJson(response);
}

export const purchasePPVOrder = async (baseUrl, apiKey, data) => {
  const response = await fetch(
    `${baseUrl}/billing/order/purchase/`,
    {
      method: "POST",
      headers: buildHeader(apiKey),
      body: withDeviceParams(data),
    }
  );

  return getJson(response);
}

export const purchaseSubscription = async (baseUrl, apiKey, data) => {
  const response = await fetch(
    `${baseUrl}/billing/subscription/subscribe/`,
    {
      method: "POST",
      headers: buildHeader(apiKey),
      body: withDeviceParams(data),
    }
  );

  return getJson(response);
};

export const completeSubscription3ds = async (baseUrl, apiKey, subscriptionId) => {
  const response = await fetch(
    `${baseUrl}/billing/subscription/complete-subscription-3ds/`,
    {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({ subscription_id: subscriptionId }),
    }
  );

  return getJson(response);
};

export const completeOrder3ds = async (baseUrl, apiKey, paymentIntentId) => {
  const response = await fetch(
    `${baseUrl}/billing/order/complete-order-3ds/`,
    {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({ payment_intent_id: paymentIntentId }),
    }
  );

  return getJson(response);
};

export const generateDpoPayPortal = async (baseUrl, apiKey, data) => {
  try {
    const response = await fetch(`${baseUrl}/billing/subscription/generate-dpo-pay-portal/`, 
      {
        method: "POST",
        headers: buildHeader(apiKey),
        body: JSON.stringify({ data }),
      });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `DPO Pay Portal request failed with status ${response.status}: ${errorData.error || "Unknown error"}`
      );
    }

    const { payment_url } = await response.json();
    return payment_url;
  } catch (error) {
    console.error("Error generating DPO Pay Portal:", error);
    throw error;
  }
};

export const verifyTransaction = async (baseUrl, apiKey,companyToken, transactionToken) => {
  try {
    // API endpoint for transaction verification
    const endpoint = `${baseUrl}/billing/subscription/verify-dpo-transaction/`;

    // Make the POST request
    const response = await fetch(endpoint, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({ companyToken, transactionToken }),
    });

    // Check if the response is successful
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Verification failed with status ${response.status}: ${
          errorData.error || "Unknown error"
        }`
      );
    }

    // Parse the JSON response
    const result = await response.json();

    return result;
  } catch (error) {
    console.error("Error during transaction verification:", error);
    throw new Error("Verification failed. Please try again later.");
  }
};

export const getActiveGateway = async (baseUrl) => { 
  const response = await fetch(`${baseUrl}/gateway-setting/list/`, {
    headers: buildSecureApiHeader(),
  });

  const gateways = await getJson(response);
  return gateways.filter(gateway => gateway.is_default)[0];
}

export const getGatewayActive = async (baseUrl, apiKey, subTenantId = null) => {
  // When buying a publisher's (sub tenant's) content, fetch that sub tenant's
  // gateway so the card is tokenized on its Stripe account.
  const url = subTenantId
    ? `${baseUrl}/gateway-setting/list-v2/?sub_tenant_id=${encodeURIComponent(subTenantId)}`
    : `${baseUrl}/gateway-setting/list-v2/`;
  const response = await fetch(url, {
    headers: buildHeader(apiKey),
  });

  const gateways = await getJson(response);
  return gateways.filter(gateway => gateway.is_default)[0];
}

export const appContentAccess = async (baseUrl, apiKey, params = {}) => {
  const queryParams = new URLSearchParams(params).toString();
  const url = queryParams 
    ? `${baseUrl}/billing/subscription/app-content/?${queryParams}`
    : `${baseUrl}/billing/subscription/app-content/`;
    
  const response = await fetch(url, {
    headers: buildHeader(apiKey),
  });

  return await getJson(response);
}

export const fetchPaypalAccessToken = async (baseUrl, apiKey, client_id, client_password) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/generate-access-token/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        client_id: client_id,
        client_secret: client_password,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return data.access_token; // Assuming the response contains the access token
    } else {
      throw new Error("Failed to fetch access token");
    }
  } catch (error) {
    console.error("Error fetching access token:", error);
    throw error;
  }
}

export const createPaypalOrder = async (baseUrl, apiKey,  orderPayload) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/create-order/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify(orderPayload),
    });

    const data = await response.json();

    if (response.ok && data.links) {
      const approvalLink = data.links.find((link) => link.rel === "approve");
      if (approvalLink) {
        return { orderId: data.id, approvalUrl: approvalLink.href };
      } else {
        throw new Error("Approval link not found.");
      }
    } else {
      throw new Error(data.message || "Failed to create PayPal order.");
    }
  } catch (error) {
    console.error("Error creating PayPal order:", error);
    throw error;
  }
};

export const capturePaypalOrder = async (baseUrl, apiKey, accessToken, orderId) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/capture-order/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken: accessToken,
        order_id: orderId,
      }),
    });

    const data = await response.json();
    console.log("Data is ", data);

    // Check if the response is OK and if the success flag is true
    if (response.ok && data.success) {
      return { success: true, response_data: data.data };
    } else {
      return { success: false, response_data: data.data };
    }
  } catch (error) {
    console.error("Error capturing PayPal order:", error);
    return false; // Return false if there is an error
  }
};

export const checkOrCreatePaypalProduct = async (baseUrl, apiKey, accessToken, productData) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/check-or-create-product/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken: accessToken,
        ...productData,
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data; // Returns the created or existing product details
    } else {
      throw new Error(data.message || "Failed to check or create product.");
    }
  } catch (error) {
    console.error("Error checking or creating PayPal product:", error);
    throw error;
  }
};

export const createPaypalSubscriptionPlan = async (baseUrl, apiKey, accessToken, planData) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/create-subscription-plan/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken: accessToken,
        ...planData,
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data; // Returns the created subscription plan details
    } else {
      throw new Error(data.message || "Failed to create subscription plan.");
    }
  } catch (error) {
    console.error("Error creating PayPal subscription plan:", error);
    throw error;
  }
};

export const createPaypalSubscription = async (baseUrl, apiKey, accessToken, subscriptionData) => {
  try {
    const response = await fetch(`${baseUrl}/billing/paypal/create-subscription/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken: accessToken,
        ...subscriptionData,
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data; // Returns subscription details including approval URL
    } else {
      throw new Error(data.message || "Failed to create subscription.");
    }
  } catch (error) {
    console.error("Error creating PayPal subscription:", error);
    throw error;
  }
};

export const getPaypalSubscriptionDetails = async (baseUrl, apiKey, accessToken, subscriptionId) => {
  try {
    console.log("getPaypalSubscriptionDetails, accessToken:", accessToken);

    const response = await fetch(`${baseUrl}/billing/paypal/subscription-details/`, {
      method: "POST", // Use POST instead of GET
      headers: buildHeader(apiKey),
      body: JSON.stringify({
        accessToken, // Send accessToken in the body
        subscription_id: subscriptionId, // Subscription ID
      }),
    });

    const data = await response.json();
    if (response.ok) {
      return data; // Return PayPal subscription details
    } else {
      throw new Error(data.message || "Failed to retrieve subscription details.");
    }
  } catch (error) {
    console.error("Error retrieving PayPal subscription details:", error);
    throw error;
  }
};

// ==================== SAVED PAYMENT METHODS (Repeat Purchase) ====================

/**
 * Get saved payment methods (cards) for the authenticated user.
 * Requires the user to be logged in.
 * 
 * Returns:
 *   - gateway: 'authorize_net' | 'stripe' | null
 *   - saved_cards: Array of saved card objects with unified fields:
 *       - customer_id: Customer ID (works for both gateways)
 *       - payment_method_id: Payment method/profile ID (works for both gateways)
 *       - display_name, card_type, last_four, expiration_date, etc.
 *   - customer_id: Unified customer ID (works for both Stripe and Authorize.Net)
 */
export const getSavedPaymentMethods = async (baseUrl, apiKey, subTenantId = null) => {
  try {
    // Saved cards are scoped to the gateway being charged; for a publisher's
    // content pass its sub_tenant_id so we read that account's cards (not the
    // parent's).
    const url = subTenantId
      ? `${baseUrl}/billing/saved-payments/list/?sub_tenant_id=${encodeURIComponent(subTenantId)}`
      : `${baseUrl}/billing/saved-payments/list/`;
    const response = await fetch(url, {
      method: "GET",
      headers: buildSecureApiHeader(),
    });

    const data = await response.json();
    if (response.ok) {
      return {
        success: true,
        gateway: data.gateway || null,
        saved_cards: data.saved_cards || [],
        customer_id: data.customer_id,
        message: data.message
      };
    } else {
      return {
        success: false,
        gateway: null,
        saved_cards: [],
        message: data.message || "Failed to retrieve saved payment methods."
      };
    }
  } catch (error) {
    console.error("Error retrieving saved payment methods:", error);
    return {
      success: false,
      gateway: null,
      saved_cards: [],
      message: error.message || "Failed to retrieve saved payment methods."
    };
  }
};

/**
 * Delete a saved payment method (card) for the authenticated user.
 * Requires the user to be logged in.
 * 
 * Args:
 *   - baseUrl: API base URL
 *   - apiKey: API key
 *   - paymentMethodId: The payment method ID to delete
 * 
 * Returns:
 *   - success: boolean
 *   - message: Status message
 */
export const validateAccessCode = async (baseUrl, apiKey, code, contentUuid, extraContentUuids = []) => {
  try {
    // Build the list of UUIDs to check (e.g. video uuid + parent VOD category uuid)
    const uuids = Array.isArray(extraContentUuids) ? [...extraContentUuids] : [];
    if (contentUuid) uuids.unshift(contentUuid);

    const response = await fetch(`${baseUrl}/billing/access-code/validate/`, {
      method: "POST",
      headers: buildHeader(apiKey),
      // Send both for backward compat with older backends that only read content_uuid.
      body: JSON.stringify({ code, content_uuid: contentUuid, content_uuids: uuids }),
    });

    const data = await response.json();
    return {
      success: !!data.valid,
      message: data.message || '',
    };
  } catch (error) {
    console.error("Error validating access code:", error);
    return { success: false, message: "Failed to validate access code." };
  }
};

export const deleteSavedPaymentMethod = async (baseUrl, apiKey, paymentMethodId) => {
  try {
    const response = await fetch(`${baseUrl}/billing/saved-payments/delete/`, {
      method: "DELETE",
      headers: buildSecureApiHeader(),
      body: JSON.stringify({ payment_method_id: paymentMethodId }),
    });

    const data = await response.json();
    if (response.ok && data.success) {
      return {
        success: true,
        message: data.message || "Payment method removed successfully."
      };
    } else {
      return {
        success: false,
        message: data.message || "Failed to remove payment method."
      };
    }
  } catch (error) {
    console.error("Error deleting saved payment method:", error);
    return {
      success: false,
      message: error.message || "Failed to remove payment method."
    };
  }
};